import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  coverageRatio,
  type StoryCovered,
  type StoryFidelity,
} from "./storyFidelity";

// ---------------------------------------------------------------------------
// StoryCoveragePanel — the customer-story ledger and how the delivered
// narration covered it (CSF-034, contract §3, §7, §8). Sits under the
// NarrationPlanPanel in the Director View.
//
// Treatment: the ledger was given to the narration author. Control: the
// ledger was built and the old narration was measured against it, but the
// author never saw it ("measured only", the A/B baseline). Treatment fallback
// (CSF-H1): the story-injected authoring failed and the narration was
// re-authored with the old context, so the ledger is measured only too.
// Legacy Echoes get a one-line empty state.
// ---------------------------------------------------------------------------

const ARM_HEADING: Record<
  "treatment" | "treatment_fallback" | "control",
  string
> = {
  treatment: "used in the narration",
  treatment_fallback:
    "not used: fell back to the old context after an authoring failure (A/B treatment)",
  control: "measured only (A/B control)",
};

const ARM_DESCRIPTION: Record<keyof typeof ARM_HEADING, string> = {
  treatment:
    "The narration author was given these story items from the customer's own words.",
  treatment_fallback:
    "Treatment arm, but authoring with these story items failed, so the narration was re-authored once with the old context; coverage measures that narration against them. A/B readouts still count this Echo as treatment (intent to treat).",
  control:
    "Control arm: the narration was written without these items; coverage measures the old narration against them (A/B baseline).",
};

const COVERED_LABELS: Record<StoryCovered, string> = {
  spoken: "spoken",
  shown: "shown",
  title_card: "title card",
  dropped: "dropped",
  missing: "missing",
};

const COVERED_CLASSES: Record<StoryCovered, string> = {
  spoken:
    "border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300",
  shown: "border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300",
  title_card:
    "border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-300",
  dropped:
    "border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300",
  missing: "border-red-300 text-red-700 dark:border-red-700 dark:text-red-300",
};

const COVERAGE_STATUS_CLASSES: Record<string, string> = {
  pass: COVERED_CLASSES.spoken,
  shortfall:
    "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300",
};

const LEDGER_STATUS_COPY: Record<string, string> = {
  ok: "ok",
  unavailable: "unavailable (the ledger could not be built)",
  empty: "empty (no story items found)",
  unverified: "unverified (items did not match the chat)",
  too_small: "too small to gate",
  disabled: "disabled by policy",
};

export function StoryCoveragePanel({ fidelity }: { fidelity: StoryFidelity }) {
  if (fidelity.kind === "loading" || fidelity.kind === "unavailable") {
    return null;
  }
  if (fidelity.kind === "legacy") {
    return (
      <p
        data-testid="story-coverage-panel"
        data-kind="legacy"
        className="mt-3 text-xs text-muted-foreground"
      >
        Customer story · not recorded for this Echo (made before story fidelity,
        or not B2C).
      </p>
    );
  }

  const ratio = coverageRatio(fidelity.coverage);
  const coverageStatus = fidelity.coverage?.status ?? null;
  const ledgerStatus = fidelity.ledgerStatus
    ? (LEDGER_STATUS_COPY[fidelity.ledgerStatus] ?? fidelity.ledgerStatus)
    : "unknown";

  return (
    <details
      data-testid="story-coverage-panel"
      data-kind={fidelity.kind}
      className="mt-3 rounded border p-3 text-xs"
    >
      <summary className="cursor-pointer font-medium">
        Customer story · {ARM_HEADING[fidelity.kind]}
        {fidelity.fallback && fidelity.kind !== "treatment_fallback"
          ? ` · fallback ${fidelity.fallback}`
          : ""}{" "}
        · ledger {fidelity.ledgerStatus ?? "unknown"}
        {ratio ? ` · ${ratio} must-say spoken` : ""}
        {coverageStatus ? (
          <Badge
            variant="outline"
            className={cn(
              "ml-2 align-middle text-[10px]",
              COVERAGE_STATUS_CLASSES[coverageStatus],
            )}
          >
            {coverageStatus}
          </Badge>
        ) : null}
      </summary>
      <div className="mt-2 space-y-2">
        <p className="text-muted-foreground">
          {ARM_DESCRIPTION[fidelity.kind]}
        </p>
        <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)]">
          {fidelity.fallback ? (
            <>
              <dt className="text-muted-foreground">Fallback</dt>
              <dd className="break-all font-mono text-[11px]">
                {fidelity.fallback}
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Ledger</dt>
          <dd>
            {ledgerStatus}
            {fidelity.itemCount !== null
              ? ` · ${fidelity.itemCount} items`
              : ""}
            {fidelity.mustSayCount !== null
              ? ` · ${fidelity.mustSayCount} must-say`
              : ""}
          </dd>
          <dt className="text-muted-foreground">Coverage</dt>
          <dd>
            {ratio
              ? `${ratio} must-say spoken${
                  fidelity.coverage?.stage
                    ? ` · stage ${fidelity.coverage.stage}`
                    : ""
                }${coverageStatus ? ` · ${coverageStatus}` : ""}`
              : "not measured"}
          </dd>
          <dt className="text-muted-foreground">Experiment</dt>
          <dd className="break-all font-mono text-[11px]">
            {fidelity.experimentId ?? "unknown"} ·{" "}
            {fidelity.ruleId ??
              (fidelity.source === "summary" ? "base value" : "rule unknown")}
          </dd>
        </dl>
        {fidelity.ledger.length > 0 ? (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="h-8">Item</TableHead>
                <TableHead className="h-8">Story detail</TableHead>
                <TableHead className="h-8">Customer's words</TableHead>
                <TableHead className="h-8">Photo</TableHead>
                <TableHead className="h-8">Must say</TableHead>
                <TableHead className="h-8">Treatment</TableHead>
                <TableHead className="h-8">Covered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fidelity.ledger.map((item) => (
                <TableRow
                  key={item.id}
                  data-testid="story-coverage-item"
                  data-covered={item.covered ?? "unmeasured"}
                >
                  <TableCell className="font-mono">{item.id}</TableCell>
                  <TableCell className="min-w-40 whitespace-normal">
                    {item.glossEn || "—"}
                  </TableCell>
                  <TableCell className="min-w-48 whitespace-normal italic">
                    {item.quote ? `“${item.quote}”` : "—"}
                  </TableCell>
                  <TableCell>{item.visual ? "yes" : "no"}</TableCell>
                  <TableCell>{item.mustSay ? "yes" : "no"}</TableCell>
                  <TableCell>
                    {item.treatment ? item.treatment.replaceAll("_", " ") : "—"}
                  </TableCell>
                  <TableCell>
                    {item.covered ? (
                      <Badge
                        variant="outline"
                        className={COVERED_CLASSES[item.covered]}
                      >
                        {COVERED_LABELS[item.covered]}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-muted-foreground">No ledger items recorded.</p>
        )}
      </div>
    </details>
  );
}
